import { useState, useEffect, useRef } from "react";
import { Form, Input, Button, App, Alert } from "antd";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { authApi, ranksApi, teamsApi } from "../../_helpers/apiService";
import Cookies from "universal-cookie";
import logoImage from "../../assets/images/logo-da.png";

// Type assertion to fix React 18 compatibility
const ReCAPTCHAFixed = ReCAPTCHA as any;

// Declare global grecaptcha type
declare global {
  interface Window {
    grecaptcha: any;
  }
}

const cookies = new Cookies();
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || "";
const IS_PRODUCTION = import.meta.env.PROD;

function Login() {
  const { message } = App.useApp();
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [buttonLoading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<any>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    // Clear any existing authentication on mount (silently)
    try {
      cookies.remove("token", { path: "/" });
      cookies.remove("refreshToken", { path: "/" });
      cookies.remove("userData", { path: "/" });
      dispatch({ type: "LOG_OUT" });
      localStorage.removeItem("persist:root");
      sessionStorage.clear();
    } catch (error) {
      console.log('Login Error:', error);
    }
  }, [dispatch]); // Run only once on mount

  const getRanks = async () => {
    try {
      const response = await ranksApi.getAll({
        limit: 1000,
        sort_by: "order",
        sort_order: "asc",
      });

      // Convert array to object keyed by id (to match Redux store format)
      const ranksObject: any = {};
      response.data.items.forEach((rank: any) => {
        ranksObject[rank.id] = rank;
      });

      return ranksObject;
    } catch (error) {
      return {};
    }
  };

  const getTeams = async () => {
    try {
      const response = await teamsApi.getAll({
        limit: 1000,
        sort_by: "name",
        sort_order: "asc",
      });

      // Convert array to object keyed by id (to match Redux store format)
      const teamsObject: any = {};
      response.data.items.forEach((team: any) => {
        teamsObject[team.id] = team;
      });

      return teamsObject;
    } catch (error) {
      return {};
    }
  };

  const onFinish = async (values: any) => {
    // Clear any previous error message
    setErrorMessage(null);

    try {
      const { username, password } = values;

      // Require reCAPTCHA in production
      if (IS_PRODUCTION && RECAPTCHA_SITE_KEY && !recaptchaToken) {
        const errMsg = "Та робот биш гэдгээ баталгаажуулна уу!";
        setErrorMessage(errMsg);
        message.error(errMsg);
        return;
      }

      if (!username || !password) {
        const errMsg = "И-мэйл болон нууц үг оруулна уу";
        setErrorMessage(errMsg);
        message.error(errMsg);
        return;
      }

      setLoading(true);

      try {
        // Login with backend API using email
        const loginResponse = await authApi.login({
          email: username, // Backend uses email for login
          password: password,
          recaptcha_token: recaptchaToken || undefined,
        });

        // Extract data from response
        const responseData = loginResponse.data;
        const access_token = responseData.access_token;
        const refresh_token = responseData.refresh_token;
        const user_id = responseData.user_id;
        const email = responseData.email;
        const first_name = responseData.first_name;
        const last_name = responseData.last_name;
        const userPhone = responseData.phone_number;
        const role = responseData.role;
        const profile_image_url = responseData.profile_image_url;

        // Create user object for storage
        const user = {
          id: user_id,
          email: email,
          first_name: first_name,
          last_name: last_name,
          phone_number: userPhone,
          role: role,
          profile_image_url: profile_image_url,
        };

        // Check user role - only allow admin, superadmin, leader, and referee
        if (!["admin", "superadmin", "leader", "referee"].includes(role)) {
          const errMsg = "Нэвтрэх эрх байхгүй байна";
          setErrorMessage(errMsg);
          message.error(errMsg);
          cookies.remove("token", { path: "/" });
          cookies.remove("refreshToken", { path: "/" });
          cookies.remove("userData", { path: "/" });
          setLoading(false);
          if (recaptchaRef.current) recaptchaRef.current.reset();
          setRecaptchaToken(null);
          return;
        }

        // Store authentication tokens in cookies with proper security settings
        const cookieOptions = {
          path: "/",
          secure: IS_PRODUCTION, // Only use secure flag in production (HTTPS)
          sameSite: "lax" as const, // Prevent CSRF attacks
          maxAge: 7 * 24 * 60 * 60, // 7 days
        };

        cookies.set("token", access_token, cookieOptions);
        cookies.set("refreshToken", refresh_token, cookieOptions);
        cookies.set("userData", JSON.stringify(user), cookieOptions);

        // Fetch ranks and teams
        const [ranks, teams] = await Promise.all([getRanks(), getTeams()]);

        // Dispatch to Redux
        dispatch({ type: "SET_USER", payload: user });
        dispatch({ type: "SET_RANKS", payload: ranks });
        dispatch({ type: "SET_TEAMS", payload: teams });

        message.success("Амжилттай нэвтэрлээ!");
        setLoading(false);
        navigate("/", { replace: true });
      } catch (error: any) {
        // Handle error messages based on status code
        let errorMessage = "Алдаа гарлаа. Дахин оролдоно уу";

        if (error.response) {
          // Server responded with error
          const status = error.response.status;

          if (status === 401) {
            // Unauthorized - wrong credentials
            errorMessage = "Та нэвтрэх нэр эсвэл нууц үгээ шалгана уу";
          } else if (status === 403) {
            errorMessage = "Нэвтрэх эрх байхгүй байна";
          } else if (status === 404) {
            errorMessage = "Хэрэглэгч олдсонгүй";
          } else if (status === 422) {
            // Validation error - format the error message properly
            const detail = error.response.data?.detail;
            if (Array.isArray(detail) && detail.length > 0) {
              // FastAPI validation errors are arrays of objects
              errorMessage = detail
                .map((err: any) => err.msg || "Validation error")
                .join(", ");
            } else if (typeof detail === "string") {
              errorMessage = detail;
            } else {
              errorMessage = "Мэдээлэл буруу байна. Талбаруудыг шалгана уу";
            }
          } else if (error.response.data?.detail) {
            // Ensure detail is a string before using it
            const detail = error.response.data.detail;
            if (typeof detail === "string") {
              errorMessage = detail;
            } else if (Array.isArray(detail)) {
              errorMessage = detail
                .map((err: any) => err.msg || err.toString())
                .join(", ");
            }
          }
        } else if (error.request) {
          // Request made but no response
          errorMessage = "Сервертэй холбогдох боломжгүй байна";
        }

        setErrorMessage(errorMessage);
        message.error(errorMessage);
        setLoading(false);

        // Reset reCAPTCHA on error
        if (recaptchaRef.current) recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
    } catch (outerError: any) {
      // Catch any unhandled errors to prevent page reload
      const errMsg = "Алдаа гарлаа. Дахин оролдоно уу";
      setErrorMessage(errMsg);
      message.error(errMsg);
      setLoading(false);
      if (recaptchaRef.current) recaptchaRef.current.reset();
      setRecaptchaToken(null);
    }
  };

  const onRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
  };

  const onFinishFailed = (errorInfo: any) => {
    const errMsg = "Талбаруудыг зөв бөглөнө үү";
    setErrorMessage(errMsg);
    message.error(errMsg);
  };

  return (
    <div className="bg-primary bg-pattern vh-100">
      <div className="account-pages pt-sm-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="text-center mb-5">
                <img src={logoImage} height="150" alt="logo" />
              </div>
            </div>
          </div>

          <div className="row justify-content-center">
            <div className="col-xl-5 col-sm-8">
              <div className="card">
                <div className="card-body p-4">
                  <div className="p-2">
                    <h5 className="mb-5 text-center">Системд нэвтрэх</h5>
                    {errorMessage && (
                      <Alert
                        message={errorMessage}
                        type="error"
                        showIcon
                        closable
                        onClose={() => setErrorMessage(null)}
                        style={{ marginBottom: 20 }}
                      />
                    )}
                    <Form
                      className="form-horizontal"
                      form={form}
                      name="login-form"
                      onFinish={onFinish}
                      onFinishFailed={onFinishFailed}
                      layout="vertical"
                    >
                      <div className="row">
                        <div className="col-md-12">
                          <div className="form-group mb-4">
                            <Form.Item
                              label="И-мэйл хаяг"
                              name="username"
                              rules={[
                                {
                                  required: true,
                                  message: "И-мэйл хаяг оруулна уу!",
                                },
                                {
                                  type: "email",
                                  message: "И-мэйл хаяг буруу байна!",
                                },
                              ]}
                            >
                              <Input
                                type="email"
                                placeholder="admin@archery.mn"
                              />
                            </Form.Item>
                          </div>

                          <div className="form-group mb-4">
                            <Form.Item
                              label="Нууц үг"
                              name="password"
                              rules={[
                                {
                                  required: true,
                                  message: "Нууц үгээ оруулна уу!",
                                },
                              ]}
                            >
                              <Input.Password />
                            </Form.Item>
                          </div>

                          <div className="form-group mb-3">
                            <div className="d-flex justify-content-center">
                              {RECAPTCHA_SITE_KEY ? (
                                <ReCAPTCHAFixed
                                  key="recaptcha-widget"
                                  ref={recaptchaRef}
                                  sitekey={RECAPTCHA_SITE_KEY}
                                  onChange={onRecaptchaChange}
                                  onExpired={() => setRecaptchaToken(null)}
                                  onErrored={() => {
                                    setRecaptchaToken(null);
                                  }}
                                />
                              ) : (
                                <div
                                  style={{ color: "#999", fontSize: "12px" }}
                                >
                                  reCAPTCHA not configured
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="mt-3">
                            <Button
                              type="primary"
                              size="large"
                              block
                              className="waves-effect waves-light"
                              htmlType="submit"
                              loading={buttonLoading}
                            >
                              Нэвтрэх
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <p className="version-code">v2.0.1 (Backend)</p>
    </div>
  );
}

export default Login;
